import { useState } from 'react';
import type { FormEvent } from 'react';
import { LoaderCircle, MailCheck } from 'lucide-react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { useSignup } from '@/api/hooks/useAuthExtra';
import { useSession } from '@/auth/session';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Free-trial signup. Mirrors LoginPage's brand panel so the two feel like one
 * flow, not two different apps stitched together.
 */
export function SignupPage() {
  const { t } = useTranslation();
  const { isAuthenticated, loading: sessionLoading } = useSession();
  const navigate = useNavigate();
  const signup = useSignup();

  const [form, setForm] = useState({
    pharmacyName: '',
    ownerName: '',
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!sessionLoading && isAuthenticated) return <Navigate to="/" replace />;

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    try {
      await signup.mutateAsync(form);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm text-center">
          <MailCheck className="mx-auto h-10 w-10 text-primary" />
          <h1 className="mt-4 font-display text-2xl">Check your email</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We sent a verification code to <strong>{form.email}</strong>. Verify it, then log in.
          </p>
          <Button className="mt-6 w-full" onClick={() => navigate('/login')}>
            Go to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-grad-gold font-display text-xl font-bold text-ink-950">
            F
          </div>
          <span className="font-display text-xl tracking-tight">{t('appName')}</span>
        </div>

        <h1 className="font-display text-3xl">Start your free trial</h1>
        <p className="mt-1 text-sm text-muted-foreground">No card required to get started.</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="pharmacyName">Pharmacy name</Label>
            <Input
              id="pharmacyName"
              required
              value={form.pharmacyName}
              onChange={(e) => set('pharmacyName', e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ownerName">Owner name</Label>
            <Input
              id="ownerName"
              required
              value={form.ownerName}
              onChange={(e) => set('ownerName', e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              placeholder="At least 8 characters"
            />
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired"
            >
              {error}
            </p>
          )}

          <Button type="submit" size="touch" className="w-full" disabled={signup.isPending}>
            {signup.isPending && <LoaderCircle className="animate-spin" />}
            {signup.isPending ? 'Creating account…' : 'Create account'}
          </Button>
        </form>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
